/**
 * Search box of the Settings page and the keymap editor (W2-13 settings-keymap): filters as you type, Escape
 * clears it (a second Escape leaves the field to the app), a clear button appears while it has text.
 *
 * While `capturing` (the keymap editor's "find a command by pressing its keys"), the owner's `onKeyDown` takes every
 * key and calls preventDefault, so no text is typed. The input stays an editable text field on purpose: the app's
 * keyboard router treats an editable text field as the owner of the keys pressed in it (DESIGN.md §8.1) and does not
 * run their commands, while a read-only input would let it run them before the editor saw the key.
 */

import type { KeyboardEvent as ReactKeyboardEvent, ReactNode, Ref } from 'react';
import { Icon, IconButton } from '../../ui';
import { m } from './settings.messages';
import s from './Settings.module.css';

export interface SearchFieldProps {
  readonly value: string;
  onChange(v: string): void;
  /** Accessible name. */
  readonly label: string;
  readonly placeholder?: string;
  readonly autoFocus?: boolean;
  /** Extra controls inside the box, after the clear button (e.g. "search by keys"). */
  readonly trailing?: ReactNode;
  onKeyDown?(e: ReactKeyboardEvent<HTMLInputElement>): void;
  readonly className?: string | undefined;
  /** Key capture replaces typing (styling only: the owner's `onKeyDown` does the capturing). */
  readonly capturing?: boolean;
  /** Id of an element describing the field (e.g. the capture hint). */
  readonly describedBy?: string | undefined;
  readonly ref?: Ref<HTMLInputElement>;
}

export function SearchField({ ref, ...props }: SearchFieldProps) {
  return (
    <div
      className={`${s.search} ${props.className ?? ''}`}
      data-capturing={props.capturing === true ? 'true' : undefined}
    >
      <Icon name={props.capturing === true ? 'Keyboard' : 'Search'} size={14} />
      <input
        ref={ref}
        type="search"
        className={s.searchInput}
        value={props.value}
        aria-label={props.label}
        aria-describedby={props.describedBy}
        placeholder={props.placeholder}
        autoFocus={props.autoFocus}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
        onKeyDown={(e) => {
          props.onKeyDown?.(e);
          if (e.defaultPrevented) return;
          if (e.key === 'Escape' && props.value !== '') {
            e.preventDefault();
            e.stopPropagation();
            props.onChange('');
          }
        }}
      />
      {props.value !== '' && (
        <IconButton
          icon="X"
          size="sm"
          label={{ key: m.searchClear }}
          onClick={() => {
            props.onChange('');
          }}
        />
      )}
      {props.trailing}
    </div>
  );
}
